<template>
  <div>
    <div class="row pt-3 l-m-b-20">
      <div class="col-12">
        <h1>Transactions</h1>
      </div>
    </div>
    <div class="row mb-3">
      <div class="col-12 l-m-b-20">
        <div class="input-group search">
          <button type="button" class="btn input-group-btn" @click="clearSearch">
            <i :class="[q ? 'fa fa-remove' : 'fa fa-search']" />
          </button>
          <input class="form-control" type="text" v-model="q" @keyup="searchText($event)" placeholder="Search" />
        </div>
      </div>
      <div class="col-12 filters-field">
        <span>Filters:</span>
        <span v-if="filtersLoaded">
          <span v-if="filters.created_at" class="filter-item btn btn-filter">
            <span class="mod-icon-filter" @click="removeFilter('created_at')" />
            Created At: {{ filters.created_at }}</span>
          <span v-if="filters.payment_method_id" class="filter-item btn btn-filter">
            <span class="mod-icon-filter" @click="removeFilter('payment_method_id')" />
            Payment Method: {{ paymentMethods.find(el => el.id === filters.payment_method_id).name }}</span>
          <span v-if="filters.payment_type_id" class="filter-item btn btn-filter">
            <span class="mod-icon-filter" @click="removeFilter('payment_type_id')" />
            Payment Type: {{ paymentTypes.find(el => el.id === filters.payment_type_id).name }}</span>
        </span>
        <button class="btn btn-secondary add-filters" @click="showFilters = true">
          <i class="fa fa-plus filter-icon" />
        </button>
      </div>
    </div>
    <template v-if="!transactions">
      <vue-content-loading :width="800" :height="450">
        <rect v-for="x in 15" :key="x" x="10" :y="15*x*1.4" rx="5" ry="5" width="720" height="6" />
      </vue-content-loading>
    </template>
    <template v-else>
      <table class="table m-table table-hover">
        <thead class="m-table__heading">
          <tr>
            <th class="m-table__id">ID</th>
            <th @click="sortBy('created_at')" :class="{ active: sortKey === 'created_at' }" class="sortable">
              Date
              <span class="arrow" :class="sortOrders['created_at'] ? 'dsc' : 'asc'" />
            </th>
            <th @click="sortBy('payment_method_id')" :class="{ active: sortKey === 'payment_method_id' }"
                class="sortable"
            >
              Method
              <span class="arrow" :class="sortOrders['payment_method_id'] ? 'dsc' : 'asc'" />
            </th>
            <th @click="sortBy('payment_type_id')" :class="{ active: sortKey === 'payment_type_id' }" class="sortable">
              Type
              <span class="arrow" :class="sortOrders['payment_type_id'] ? 'dsc' : 'asc'" />
            </th>
            <th>Ref ID</th>
            <th @click="sortBy('amount')" :class="{ active: sortKey === 'amount' }" class="sortable">
              Amount
              <span class="arrow" :class="sortOrders['amount'] ? 'dsc' : 'asc'" />
            </th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(transaction, index) in transactions"
              :key="`trans-prefix-${index}`"
              @mouseup="$_can('job-charges.show') && view($event, transaction.id)"
              :class="{pointer: $_can('job-charges.show')}"
          >
            <td>{{ transaction.id }}</td>
            <td>{{ transaction.date }}</td>
            <td>{{ transaction.payment_method.name }}</td>
            <td>{{ transaction.payment_type.name }}</td>
            <td>{{ transaction.ref_id }}</td>
            <td>$ {{ transaction.total }}</td>
            <td class="position-relative">
              <button class="btn btn-link p-0 tippy-popup" :class="!transaction.note && 'text-secondary'"
                      @mouseover="showNote(index)" @mouseout="hideNote(index)"
              >
                <i class="fa fa-sticky-note" />
              </button>
              <div class="popover" v-if="note[index] && transaction.note">
                <p>{{ transaction.note }}</p>
              </div>
            </td>
          </tr>
          <tr v-if="transactions && !transactions.length">
            <td colspan="8" class="text-center">
              No results found
            </td>
          </tr>
        </tbody>
      </table>

      <div class="row">
        <div class="col-sm-6 mb-1 text-black-50"
             v-if="meta.total"
        >
          Showing {{ meta.from }} - {{ meta.to }} of {{ meta.total }}
        </div>
        <pagination class="col-sm-6 mb-1" :change-page="changePage" :meta="meta" />
      </div>
    </template>

    <!-- Filters -->
    <transition name="slide-fade">
      <div class="l-modal-mask" v-if="showFilters" @click.self="showFilters = false">
        <div class="filters-side-modal flex flex-column">
          <div @click="showFilters = !showFilters" class="pointer">
            <h1 class="modal-h1 l-m-b-20"><span class="btn btn-back" />Add Filter</h1>
          </div>
          <div>
            <p>
              Filters are applied automatically. You can remove them one by one with minus button or with clear filters button.
            </p>
          </div>
          <div class="l-bg-white">
            <div class="row">
              <div class="col">
                <div class="row filters">
                  <div class="col-12">
                    <div class="select-wrap select-wrap-calendar">
                      <label>Created At</label>
                      <flat-pickr v-model="filters.created_at"
                                  :config="config"
                                  class="simple-select-input"
                                  placeholder="Select Created Date"
                                  name="date"
                                  @on-close="onCreatedDateChange"
                      />
                      <span class="position-absolute clear-date" @click="clearCreatedDateAndSearch"
                            v-if="filters.created_at"
                      >
                        <span class="mod-icon-clear" />
                        <span aria-hidden="true" class="sr-only">Clear</span>
                      </span>
                      <span class="position-absolute clear-date clear-date-transparent" v-else><span
                        class="mod-icon-rates-dark"
                      /></span>
                    </div>
                  </div>
                  <div class="col-12">
                    <div class="select-wrap">
                      <label>Payment method</label>
                      <simple-select v-model="filters.payment_method_id"
                                     :options="paymentMethods"
                                     placeholder="Select Payment Method"
                                     @input="search"
                      />
                    </div>
                  </div>
                  <div class="col-12">
                    <div class="select-wrap">
                      <label>Payment type</label>
                      <simple-select v-model="filters.payment_type_id"
                                     :options="paymentTypes"
                                     placeholder="Select Payment Type"
                                     @input="search"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row mt-auto">
            <div class="col">
              <button type="button" class="btn btn-not-active text-dark w-100" @click="clearFilters"
                      v-if="!(!filters.created_at && !filters.payment_type_id &&
                        !filters.payment_method_id)"
              >
                Clear filters
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import store from 'store';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import flatPickr from 'vue-flatpickr-component';
import Pagination from '../Pagination';
import 'flatpickr/dist/flatpickr.css';

export default {
  name: 'Transactions',
  components: {
    Pagination,
    flatPickr,
  },
  data: () => ({
    filters: {
      created_at: '',
      createdDateStart: '',
      createdDateEnd: '',
      payment_method_id: '',
      payment_type_id: '',
    },
    config: {
      dateFormat: 'm/j/Y',
      mode: 'range',
    },
    sortKey: '',
    sortOrders: [],
    page: 1,
    q: '',
    timer: null,
    payment_methods: {},
    payment_types: {},
    companyId: store.get('dispatchUser').company_id,
    note: [],
    showFilters: false,
  }),
  computed: {
    ...mapState({
      transactions: state => state.transactions.transactions.data,
      meta: state => state.transactions.transactions.meta,
      paymentMethods: state => state.values.paymentMethods,
      paymentTypes: state => state.values.paymentTypes,
      filtersSaved: state => state.filters.filtersSaved,
      filtersLoaded: state => state.guard.filtersLoaded,
    }),
    ...mapGetters(['getTransactionsFiltered', 'getFiltersSaved']),
  },
  created() {
    this.getPaymentMethods();
    this.getPaymentTypes();
  },
  mounted() {
    if (this.filtersSaved && this.filtersSaved.previousPage === 'TransactionsEdit') {
      this.filters = { ...this.filtersSaved };
      this.page = this.filtersSaved.page;
      this.filter();
    } else {
      if (Object.keys(this.$route.query).length !== 0) {
        this.setFilters(this.$route.query);
      }
      this.filter();
    }
  },
  methods: {
    ...mapActions(['getTransactions', 'getPaymentMethods', 'getPaymentTypes']),
    ...mapMutations(['setTransactions']),
    setFilters(query) {
      this.filters = Object.assign(
        {
          createdDateStart: query.created_from.toString(),
          createdDateEnd: query.created_to.toString(),
          payment_method_id: query.payment_method_id ? parseInt(query.payment_method_id, 10) : '',
          payment_type_id: query.payment_type_id ? parseInt(query.payment_type_id, 10) : '',
          created_at:
            query.created_from && query.created_to
              ? `${query.created_from.toString()} to ${query.created_from.toString()}`
              : '',
        },
        {},
      );
      this.page = query.page;
      this.sortKey = query.sortKey;
      this.sortOrders = query.order;
      this.q = query.q.toString();
    },
    changePage(pageNum) {
      this.setTransactions({});
      this.page = pageNum;
      this.filter();
    },
    view(event, id) {
      if (event.button === 1) {
        window.open(this.$router.resolve(`/transactions/edit/${id}`).href, '_blank');
      } else {
        this.$router.push(`/transactions/edit/${id}`);
      }
    },
    showNote(index) {
      this.$set(this.note, index, true);
    },
    hideNote(index) {
      this.$set(this.note, index, false);
    },
    sortBy(key) {
      this.sortKey = key;
      this.$set(this.sortOrders, key, !this.sortOrders[key]);
      this.filter();
    },
    searchText(e) {
      this.page = 1;
      if (/^[a-zA-Z0-9]$/.test(e.key) || e.keyCode === 8 || e.keyCode === 13) {
        const vm = this;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => {
          vm.filter();
        }, 500);
      }
    },
    search() {
      this.page = 1;
      this.filter();
    },
    filter() {
      const query =
        `page=${this.page}&sort=${this.sortKey}` +
        `&order=${this.sortOrders[this.sortKey] ? 'asc' : 'desc'}&q=${encodeURIComponent(
          this.q,
        )}&created_from=${this.filters.createdDateStart}&created_to=${
          this.filters.createdDateEnd
        }&payment_method_id=${this.filters.payment_method_id}&payment_type_id=${
          this.filters.payment_type_id
        }`;
      this.getTransactionsFiltered(query);
      if (this.$_checkProperties(this.filters)) {
        this.$router.push(`/transactions?${query}`);
      } else {
        this.$router.push(`/transactions`);
      }
    },
    clearFilters() {
      this.filters = Object.assign(
        {
          payment_type_id: '',
          payment_method_id: '',
          created_at: '',
          createdDateStart: '',
          createdDateEnd: '',
        },
        {},
      );
      this.sortKey = '';
      this.sortOrders = [];
      this.filter();
      this.showFilters = false;
    },
    clearSearch() {
      this.q = '';
      this.filter();
    },
    clearCreatedDateAndSearch() {
      this.filters.created_at = '';
      this.filters.createdDateStart = '';
      this.filters.createdDateEnd = '';
      this.page = 1;
      this.filter();
    },
    onCreatedDateChange(selectedDates) {
      this.filters.createdDateStart = selectedDates[0]
        ? selectedDates[0].toLocaleDateString('en-US')
        : '';
      this.filters.createdDateEnd = selectedDates[1]
        ? selectedDates[1].toLocaleDateString('en-US')
        : '';
      this.search();
    },
    removeFilter(model) {
      this.filters[model] = '';
      if (model === 'created_at') {
        this.filters.createdDateStart = '';
        this.filters.createdDateEnd = '';
      }
      this.search();
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/transactions/Transactions.vue